import clsx from 'clsx'
import { ReactNode } from 'react'

interface LoadingButtonProps {
  children: ReactNode
  loading?: boolean
  disabled?: boolean
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  type?: 'button' | 'submit' | 'reset'
  className?: string
  loadingText?: string
  onClick?: () => void
}

export function LoadingButton({
  children,
  loading = false,
  disabled = false,
  variant = 'primary',
  size = 'md',
  type = 'button',
  className,
  loadingText,
  onClick,
}: LoadingButtonProps) {
  const isDisabled = loading || disabled

  const baseClasses =
    'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed'

  const variantClasses = {
    primary:
      'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500 shadow-sm',
    secondary:
      'bg-white text-stone-700 border border-stone-300 hover:bg-stone-50 focus:ring-teal-500',
    ghost:
      'text-stone-600 hover:bg-stone-100 focus:ring-stone-500',
    danger:
      'bg-error-600 text-white hover:bg-error-700 focus:ring-error-500 shadow-sm',
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  }

  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {loading && (
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {loading && loadingText ? loadingText : children}
    </button>
  )
}

// Icon button variant for actions
interface IconButtonProps {
  icon: ReactNode
  loading?: boolean
  disabled?: boolean
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  className?: string
  title?: string
  onClick?: () => void
}

export function IconButton({
  icon,
  loading = false,
  disabled = false,
  variant = 'ghost',
  size = 'md',
  className,
  title,
  onClick,
}: IconButtonProps) {
  const isDisabled = loading || disabled

  const baseClasses =
    'inline-flex items-center justify-center rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed'

  const variantClasses = {
    primary:
      'bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-500',
    secondary:
      'bg-white text-stone-700 border border-stone-300 hover:bg-stone-50 focus:ring-teal-500',
    ghost:
      'text-stone-500 hover:text-stone-700 hover:bg-stone-100 focus:ring-stone-500',
    danger:
      'text-error-600 hover:text-error-700 hover:bg-error-50 focus:ring-error-500',
  }

  const sizeClasses = {
    sm: 'p-1',
    md: 'p-2',
    lg: 'p-3',
  }

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      title={title}
      className={clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {loading ? (
        <svg
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        icon
      )}
    </button>
  )
}
